// @flow
import * as React from 'react';
import type { ChildrenArray } from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import { figureSizes } from '../../designSystem.js';
import { isElement } from 'react-is';
import Image from '../../Image.js';
import DraftImage from '../../DraftImage.js';
import BoxAndWhiskerPlot from '../../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../../CartesianPlane.js';
import { NumberLine } from '../../NumberLine.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';

export function RSLMultipleChoiceImageOptions(props: {|
  children: ChildrenArray<React$Element<typeof RSLMultipleChoiceImageOption>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLMultipleChoiceImageOptions,
  );

  let errors = null;
  if (failedValidation === false && props._performValidation) {
    // We only want to do this validation if the .spec interface checks haven't
    // failed as that ensures that all our children are the correct type
    errors = validateChildren(props.children, locations);
  }

  React.useEffect(() => {
    if (errors !== null) addErrors(errors);
  });

  if (failedValidation) return null;
  if (errors !== null) return null;

  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', gap: 32, margin: '28px 0' }}
    >
      {React.Children.toArray(props.children).map((c, i) =>
        React.cloneElement(c, {
          _label: String.fromCharCode('A'.charCodeAt(0) + i),
        }),
      )}
    </div>
  );
}
RSLMultipleChoiceImageOptions.spec = {
  children: childrenArrayMatcher({
    min: 2,
    max: 6,
    elementType: t.elementType(RSLMultipleChoiceImageOption),
  }),
};

export function RSLMultipleChoiceImageOption(props: {|
  _label: string,
  children:
    | React$Element<typeof Image>
    | React$Element<typeof DraftImage>
    | React$Element<typeof BoxAndWhiskerPlot>
    | React$Element<typeof CartesianPlane>
    | React$Element<typeof NumberLine>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLMultipleChoiceImageOption,
  );
  if (failedValidation) return null;

  // Should only be a single child by this point in time, but we'll
  // handle the general case for robustness.
  const clonedChildren = React.Children.toArray(props.children).map((child) =>
    React.cloneElement(child, {
      _removeFigureMargin: true,
    }),
  );

  const componentName = getComponentName(clonedChildren[0]);
  // Components that don't tend to have a clearly perceivable shape
  // get a light background to aid perception of the option's shape.
  const showBackground = new Set(['Image', 'DraftImage', 'NumberLine']).has(
    componentName,
  );

  return (
    <div
      style={{
        display: 'inline-flex',
        background: showBackground ? '#f5f5f5' : 'transparent',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 24,
          fontWeight: 600,
          // When showing the background we need a little more breathing room
          // for the text.
          ...(showBackground
            ? {
                textAlign: 'center',
                paddingTop: 4,
              }
            : {}),
        }}
      >
        {props._label}
      </div>
      {clonedChildren}
    </div>
  );
}
RSLMultipleChoiceImageOption.spec = {
  children: t.unionOfTypes(
    t.elementType(Image),
    t.elementType(DraftImage),
    t.elementType(BoxAndWhiskerPlot),
    t.elementType(CartesianPlane),
    t.elementType(NumberLine),
  ),
};

const allowedChildComponentNames = new Set([
  'Image',
  'DraftImage',
  'BoxAndWhiskerPlot',
  'CartesianPlane',
  'NumberLine',
]);

type Locations = {
  [propOrComponentName: string]: {|
    startLineNumber: number,
    startColumn: number,
    endLineNumber: number,
    endColumn: number,
  |},
};

// Precondition: each child is of type RSLMultipleChoiceImageOption
function validateChildren(
  children: ChildrenArray<React$Element<typeof RSLMultipleChoiceImageOption>>,
  locations: Locations,
): Array<{}> | null {
  const imageOptions = React.Children.toArray(children);

  // Ensure every RSLMultipleChoiceImageOption has precisely 1 child
  // whose type is one of those in the set of allowedChildComponentNames.
  for (const option of imageOptions) {
    const optionChildren = React.Children.toArray(option.props.children);
    if (optionChildren.length !== 1) {
      return [
        {
          message: `${RSLMultipleChoiceImageOption.name} must have precisely 1 child`,
          ...option.props._sourceLocations?.RSLMultipleChoiceImageOption,
        },
      ];
    }

    const [child] = optionChildren;
    const componentName = getComponentName(child);
    if (allowedChildComponentNames.has(componentName) === false) {
      const allowedComponentsList = [
        ...allowedChildComponentNames.values(),
      ].join(', ');
      return [
        {
          message: `The child of ${RSLMultipleChoiceImageOption.name} must be one of these: ${allowedComponentsList}`,
          ...(componentName === ''
            ? option.props._sourceLocations?.RSLMultipleChoiceImageOption
            : child.props._sourceLocations?.[componentName]),
        },
      ];
    }
  }

  const imageLikeElements = imageOptions.map((option) =>
    React.Children.only(option.props.children),
  );
  const imageLikeComponentNames = new Set(
    imageLikeElements.map((imageLikeElement) =>
      getComponentName(imageLikeElement),
    ),
  );

  // Ensure all image-like components used in the RSLMultipleChoiceImageOption
  // components are the same type.
  if (imageLikeComponentNames.size > 1) {
    return [
      {
        message: [
          `The child of every ${RSLMultipleChoiceImageOption.name} must be the same component.`,
          `You have used ${imageLikeComponentNames.size} different component types:`,
          `${[...imageLikeComponentNames.values()].join(', ')}`,
        ].join(' '),
        ...locations.RSLMultipleChoiceImageOptions,
      },
    ];
  }

  const dimensions = imageLikeElements.map(({ props }) => ({
    width: props.width,
    height: props.height,
  }));

  // Ensure numeric values for width & height are provided for all child components,
  // and that the dimensions are allowed by our design system.
  for (const [index, { width, height }] of dimensions.entries()) {
    const element = imageLikeElements[index];
    if (width === undefined || height === undefined) {
      return [
        {
          message: 'width and height props must both be explicitly specified',
          ...element.props._sourceLocations?.[getComponentName(element)],
        },
      ];
    }

    if (typeof width !== 'number') {
      return [
        {
          message: 'width must be a number',
          ...element.props._sourceLocations?.width,
        },
      ];
    }

    if (typeof height !== 'number') {
      return [
        {
          message: 'height must be a number',
          ...element.props._sourceLocations?.height,
        },
      ];
    }

    const isFigureSizeAllowed = figureSizes.some(
      (figureSize) =>
        figureSize.width === width && figureSize.height === height,
    );
    if (isFigureSizeAllowed === false) {
      const allowedSizesList = figureSizes
        .map(({ width, height }) => `${width}×${height}`)
        .join('\n  - ');
      return [
        {
          message: `width×height must be one of:\n  - ${allowedSizesList}`,
          ...element.props._sourceLocations?.[getComponentName(element)],
        },
      ];
    }
  }

  // Ensure the width/height props are identical for all components
  const widthSet = new Set(dimensions.map(({ width }) => width));
  const heightSet = new Set(dimensions.map(({ height }) => height));
  if (widthSet.size > 1 || heightSet.size > 1) {
    return [
      {
        message: `The child of every ${RSLMultipleChoiceImageOption.name} must specify the same width & height.`,
        ...locations.RSLMultipleChoiceImageOptions,
      },
    ];
  }

  return null;
}

function getComponentName(element) {
  // Non-elements (such as strings) don't have a name
  if (isElement(element) === false) {
    return '';
  }

  // This is that regretable pattern introduced for the CartesianPlane HOC
  if (element.type.__wrappedComponent) {
    return element.type.__wrappedComponent.name;
  }

  return element.type.name;
}

// Make it clear that these are not part of the public API and are only
// exported for the purpose of importing into test files.
export const _testExports = {
  validateChildren,
};
